import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  closeLoaderWithMessage,
  openLoaderWithMessage,
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { makeApiCall } from "../../helpers/postRequest";
import {
  IUserMapRecord,
  ISetUserMapPayload,
  ISetUserMapResponse,
} from "./userMapping.types";
import { api } from "../../api/api";
import { action } from "typesafe-actions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

// ==================== Action Type Constants ====================

export const FETCH_USER_MAP_LIST_PROGRESS = "FETCH_USER_MAP_LIST_PROGRESS";
export const FETCH_USER_MAP_LIST_SUCCESS = "FETCH_USER_MAP_LIST_SUCCESS";
export const FETCH_USER_MAP_LIST_FAILURE = "FETCH_USER_MAP_LIST_FAILURE";

export const FETCH_USER_MAP_DETAILS_PROGRESS = "FETCH_USER_MAP_DETAILS_PROGRESS";
export const FETCH_USER_MAP_DETAILS_SUCCESS = "FETCH_USER_MAP_DETAILS_SUCCESS";
export const FETCH_USER_MAP_DETAILS_FAILURE = "FETCH_USER_MAP_DETAILS_FAILURE";

export const CLEAR_USER_MAPPING_STATE = "CLEAR_USER_MAPPING_STATE";

// ==================== Action Creators ====================

export const fetchUserMapListProgress = () =>
  action(FETCH_USER_MAP_LIST_PROGRESS);

export const fetchUserMapListSuccess = (
  data: IUserMapRecord[],
  totalRecords: number
) => action(FETCH_USER_MAP_LIST_SUCCESS, { data, totalRecords });

export const fetchUserMapListFailed = (errorMessage: string) =>
  action(FETCH_USER_MAP_LIST_FAILURE, { errorMessage });

export const fetchUserMapDetailsProgress = () =>
  action(FETCH_USER_MAP_DETAILS_PROGRESS);

export const fetchUserMapDetailsSuccess = (
  data: IUserMapRecord[],
  totalRecords: number
) => action(FETCH_USER_MAP_DETAILS_SUCCESS, { data, totalRecords });

export const fetchUserMapDetailsFailed = (errorMessage: string) =>
  action(FETCH_USER_MAP_DETAILS_FAILURE, { errorMessage });

export const clearUserMappingState = () => action(CLEAR_USER_MAPPING_STATE);

// ==================== Thunks ====================

export const getExcelColumnMappingWithAIForUserMapAsync =
  (
    data: any,
    onCallback: (isSuccess: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(openLoaderWithMessage("Mappings are being Generated..."));

      const res = await makeApiCall(
        {
          url: "/import-utility/excel-mapping-for-user-map",
          method: "POST",
        },
        undefined,
        {
          excel_headers: [data],
        }
      );

      const finalDocument: any = res.data.data;

      dispatch(
        showMessage({
          type: "success",
          message: "Mappings Generated successfully",
          displayAs: "snackbar",
        })
      );
      onCallback(true, finalDocument);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(closeLoaderWithMessage());
    }
  };

export const upsertUserMapDocument =
  (
    data: ISetUserMapPayload,
    onCallback: (isSuccess: boolean, data?: ISetUserMapResponse) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/import-utility/set-use-map", data);

      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchUserMapListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    userMapCode?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchUserMapListProgress());
      let finalUrl = `/import-utility/get-user-map?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl += `&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      if (userMapCode) {
        finalUrl += `&user_map_code=${userMapCode}`;
      }

      const res = await api.get(finalUrl);
      const data: IUserMapRecord[] = res.data.data;
      dispatch(fetchUserMapListSuccess(data, res.data.totalRecords));
    } catch (err: any) {
      dispatch(fetchUserMapListFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const fetchUserMapDetailsAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    searchValue: IDatatableV2AdvancedSearchFilter,
    userMapCode: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchUserMapDetailsProgress());
      let finalUrl = `/import-utility/get-user-map-details?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&user_map_code=${userMapCode}`;
      if (searchValue.length > 0) {
        finalUrl += `&advanceFilter=${JSON.stringify(searchValue)}`;
      }

      const res = await api.get(finalUrl);
      const data: IUserMapRecord[] = res.data.data;
      dispatch(fetchUserMapDetailsSuccess(data, res.data.totalRecords));
    } catch (err: any) {
      dispatch(fetchUserMapDetailsFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
